import Form, {
  ButtonItem,
  ButtonOptions,
  Item,
  Label,
  RequiredRule,
} from "devextreme-react/form";
import LoadIndicator from "devextreme-react/load-indicator";
import notify from "../../lib/notify";
import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { changePasswordFirstLogin } from "../../api/auth";
import { useAuth } from "../../contexts/auth";
import "./FirstLoginChangePassword.scss";

export const FirstLoginChangePassword = ({ redirectLink = "/sign-in" }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { signIn, signOut } = useAuth();

  const [mode, setMode] = useState<"password" | "text">("password");

  const email = location.state?.email || "";
  const password = location.state?.password || "";
  const companyId = location.state?.companyId;
  const rememberMe: boolean = location.state?.rememberMe ?? false;

  const handleLogoutClick = async (e) => {
    e.preventDefault();
    signOut();
    navigate(redirectLink);
  };
  const formData = useRef({
    newPassword: "",
    confirmPassword: "",
  });

  const [loading, setLoading] = useState(false);

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setLoading(true);

      const { newPassword, confirmPassword } = formData.current;

      if (newPassword !== confirmPassword) {
        notify(t("Passwords do not match"), "error", 5000);
        setLoading(false);
        return;
      }

      try {
        await changePasswordFirstLogin(companyId, email, newPassword);

        const result = await signIn(email, newPassword, companyId!, rememberMe);
        if (result.isOk) {
          if (result.enable2Fa) {
            navigate("/sign-in", {
              state: {
                email,
                password: newPassword,
                companyId,
                rememberMe,
                autoSignIn: true,
              },
            });
          } else {
            navigate("/");
          }
        } else {
          notify(result.message || t("common.unknownErrorOccurred"), "error", 5000);
        }
      } catch (error) {
        notify(
          error instanceof Error
            ? error.message
            : t("common.unknownErrorOccurred"),
          "error",
          5000,
        );
      } finally {
        setLoading(false);
      }
    },
    [companyId, email, navigate, rememberMe, signIn, t],
  );

  return (
    <form className="first-login-form" onSubmit={onSubmit}>
      <Form
        formData={formData.current}
        disabled={loading}
        onFieldDataChanged={(e) => {
          if (e.dataField) {
            formData.current[e.dataField] = e.value;
          }
        }}
      >
        <Item
          render={() => (
            <div className="account-blocked-info">
              <p className="text-justify">
                {t(
                  "This is your first login. To log in, please provide your Password.",
                )}
              </p>
              <p className="font-bold text-justify">
                {t("employeeList.passwordHelpText")}
              </p>
            </div>
          )}
        />
        <Item
          dataField="newPassword"
          editorType="dxTextBox"
          editorOptions={{
            placeholder: t("Enter your new password"),
            mode: mode,
            buttons: [
              {
                name: "passwordToggle",
                location: "after",
                options: {
                  icon: mode === "password" ? "eyeopen" : "eyeclose",
                  stylingMode: "text",
                  onClick: () => {
                    setMode((prev) =>
                      prev === "password" ? "text" : "password",
                    );
                  },
                },
              },
            ],
          }}
        >
          <RequiredRule message={t("New password is required")} />
          <Label text={t("New password")} />
        </Item>
        <Item
          dataField="confirmPassword"
          editorType="dxTextBox"
          editorOptions={{
            placeholder: t("Confirm your new password"),
            mode: mode,
          }}
        >
          <RequiredRule message={t("Password confirmation is required")} />
          <Label text={t("Confirm new password")} />
        </Item>
        <ButtonItem>
          <ButtonOptions width="100%" type="default" useSubmitBehavior>
            <span className="dx-button-text">
              {loading ? (
                <LoadIndicator width="24px" height="24px" visible />
              ) : (
                t("submit")
              )}
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
      <div className="first-login-link">
        <a href={redirectLink} onClick={handleLogoutClick}>
          {t("Back to login")}
        </a>
      </div>
    </form>
  );
};
